import { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, X, Check, CheckCheck, Filter, Settings, Volume2, VolumeX, ChevronRight, AlertTriangle, Info, MessageSquare, Users, FileText, Star, Zap, Megaphone, Calendar, Clock, Wifi, WifiOff, BellRing, BellOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { usePushNotifications } from '@/hooks/usePushNotifications';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Get WebSocket URL from API URL
const getWebSocketUrl = () => {
  const url = new URL(API_URL || window.location.origin);
  const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${url.host}`;
};

// Notification type icons and colors
const notificationConfig = {
  note: { icon: FileText, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  score: { icon: Star, color: 'text-amber-500', bg: 'bg-amber-500/10' },
  coaching: { icon: Zap, color: 'text-purple-500', bg: 'bg-purple-500/10' },
  review: { icon: CheckCheck, color: 'text-green-500', bg: 'bg-green-500/10' },
  user: { icon: Users, color: 'text-cyan-500', bg: 'bg-cyan-500/10' },
  alert: { icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-500/10' },
  ticket: { icon: MessageSquare, color: 'text-orange-500', bg: 'bg-orange-500/10' },
  chat: { icon: MessageSquare, color: 'text-indigo-500', bg: 'bg-indigo-500/10' },
  announcement: { icon: Megaphone, color: 'text-pink-500', bg: 'bg-pink-500/10' },
  task: { icon: Check, color: 'text-teal-500', bg: 'bg-teal-500/10' },
  system: { icon: Info, color: 'text-slate-500', bg: 'bg-slate-500/10' },
};

const priorityColors = {
  urgent: 'border-l-4 border-l-red-500 bg-red-50 dark:bg-red-950/20',
  high: 'border-l-4 border-l-orange-500 bg-orange-50 dark:bg-orange-950/20',
  normal: 'border-l-4 border-l-transparent',
  low: 'border-l-4 border-l-transparent opacity-80',
};

export default function NotificationBell({ userId, userRole = 'user', tenantId = null }) {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState({ total_unread: 0, urgent: 0, high: 0 });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [daysFilter, setDaysFilter] = useState('0'); // 0 = today
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [wsConnected, setWsConnected] = useState(false);
  const [pushEnabling, setPushEnabling] = useState(false);
  const audioRef = useRef(null);
  const prevCountRef = useRef(0);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const navigate = useNavigate();

  // Push notification hook
  const {
    isSupported: pushSupported,
    isSubscribed: pushSubscribed,
    permission: pushPermission,
    loading: pushLoading,
    subscribe: subscribePush,
    unsubscribe: unsubscribePush,
    sendTest: sendTestPush
  } = usePushNotifications(userId);

  // Handle push toggle
  const handlePushToggle = async () => {
    setPushEnabling(true);
    try {
      if (pushSubscribed) {
        await unsubscribePush();
      } else {
        const success = await subscribePush();
        if (success) {
          // Send test notification
          setTimeout(() => sendTestPush(), 1000);
        }
      }
    } catch (err) {
      console.error('Push toggle error:', err);
    } finally {
      setPushEnabling(false);
    }
  };

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }, [soundEnabled]);

  // Handle incoming WebSocket messages
  const handleWebSocketMessage = useCallback((event) => {
    try {
      const message = JSON.parse(event.data);
      
      switch (message.type) {
        case 'connected':
          console.log('WebSocket connected for notifications');
          setWsConnected(true);
          reconnectAttemptsRef.current = 0;
          break;
          
        case 'notification':
          // Add new notification to the list (prepend)
          const newNotification = message.data;
          setNotifications(prev => {
            // Avoid duplicates
            if (prev.some(n => n.notification_id === newNotification.notification_id)) {
              return prev;
            }
            return [{ ...newNotification, is_read: false }, ...prev];
          });
          
          // Update unread count
          setUnreadCount(prev => ({
            ...prev,
            total_unread: prev.total_unread + 1,
            urgent: newNotification.priority === 'urgent' ? prev.urgent + 1 : prev.urgent,
            high: newNotification.priority === 'high' ? prev.high + 1 : prev.high
          }));
          
          // Play sound for new notifications
          playNotificationSound();
          break;
          
        case 'unread_count':
          setUnreadCount(message.data);
          break;
          
        case 'pong':
          // Keep-alive response, no action needed
          break;
          
        default:
          console.log('Unknown WebSocket message type:', message.type);
      }
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error);
    }
  }, [playNotificationSound]);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    if (!userId || wsRef.current?.readyState === WebSocket.OPEN) return;
    
    const wsUrl = `${getWebSocketUrl()}/api/ws/notifications?user_id=${userId}&role=${userRole}${tenantId ? `&tenant_id=${tenantId}` : ''}`;
    
    try {
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('WebSocket connection established');
        setWsConnected(true);
      };
      
      ws.onmessage = handleWebSocketMessage;
      
      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        setWsConnected(false);
        wsRef.current = null;
        
        // Attempt to reconnect with exponential backoff
        if (reconnectAttemptsRef.current < 5) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current++;
            connectWebSocket();
          }, delay);
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
      
      wsRef.current = ws;
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
    }
  }, [userId, userRole, tenantId, handleWebSocketMessage]);

  // Send ping to keep connection alive
  const sendPing = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'ping' }));
    }
  }, []);

  // Fetch unread count (fallback when WebSocket not connected)
  const fetchUnreadCount = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(
        `${API_URL}/api/notifications/unread-count?user_id=${userId}&role=${userRole}${tenantId ? `&tenant_id=${tenantId}` : ''}`
      );
      if (res.ok) {
        const data = await res.json();
        
        // Play sound if new notifications arrived
        if (soundEnabled && data.total_unread > prevCountRef.current && prevCountRef.current > 0) {
          playNotificationSound();
        }
        prevCountRef.current = data.total_unread;
        
        setUnreadCount(data);
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  }, [userId, userRole, tenantId, soundEnabled, playNotificationSound]);

  // Fetch notifications
  const fetchNotifications = useCallback(async (resetPage = false) => {
    if (!userId) return;
    setLoading(true);
    try {
      const currentPage = resetPage ? 1 : page;
      const typeFilter = activeTab !== 'all' && activeTab !== 'unread' ? `&type_filter=${activeTab}` : '';
      const unreadOnly = activeTab === 'unread' ? '&unread_only=true' : '';
      
      const res = await fetch(
        `${API_URL}/api/notifications?user_id=${userId}&role=${userRole}${tenantId ? `&tenant_id=${tenantId}` : ''}&days=${daysFilter}&page=${currentPage}&limit=20${typeFilter}${unreadOnly}`
      );
      if (res.ok) {
        const data = await res.json();
        if (resetPage) {
          setNotifications(data.notifications);
          setPage(1);
        } else if (currentPage === 1) {
          setNotifications(data.notifications);
        } else {
          setNotifications(prev => [...prev, ...data.notifications]);
        }
        setHasMore(data.has_more);
        setTotalCount(data.total);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [userId, userRole, tenantId, daysFilter, page, activeTab]);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await fetch(
        `${API_URL}/api/notifications/mark-read/${notificationId}?user_id=${userId}`,
        { method: 'POST' }
      );
      setNotifications(prev =>
        prev.map(n => n.notification_id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => ({
        ...prev,
        total_unread: Math.max(0, prev.total_unread - 1)
      }));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const typeFilter = activeTab !== 'all' && activeTab !== 'unread' ? `&type_filter=${activeTab}` : '';
      await fetch(
        `${API_URL}/api/notifications/mark-all-read?user_id=${userId}&role=${userRole}${tenantId ? `&tenant_id=${tenantId}` : ''}${typeFilter}`,
        { method: 'POST' }
      );
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount({ total_unread: 0, urgent: 0, high: 0, normal: 0 });
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.notification_id);
    }
    if (notification.action_url) {
      navigate(notification.action_url);
      setIsOpen(false);
    }
  };

  // Load more
  const loadMore = () => {
    setPage(prev => prev + 1);
  };

  // Initialize WebSocket connection
  useEffect(() => {
    if (userId) {
      connectWebSocket();
      // Also fetch initial unread count
      fetchUnreadCount();
    }
    
    return () => {
      // Cleanup WebSocket on unmount
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [userId, connectWebSocket, fetchUnreadCount]);

  // Keep-alive ping every 25 seconds
  useEffect(() => {
    const interval = setInterval(sendPing, 25000);
    return () => clearInterval(interval);
  }, [sendPing]);

  // Fallback polling when WebSocket is not connected
  useEffect(() => {
    if (!wsConnected) {
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [wsConnected, fetchUnreadCount]);

  // Fetch notifications when panel opens or filters change
  useEffect(() => {
    if (isOpen) {
      fetchNotifications(true);
    }
  }, [isOpen, daysFilter, activeTab]);

  // Fetch more when page changes
  useEffect(() => {
    if (page > 1) {
      fetchNotifications(false);
    }
  }, [page]);

  // Format relative time
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const NotificationItem = ({ notification }) => {
    const config = notificationConfig[notification.type] || notificationConfig.system;
    const Icon = config.icon;

    return (
      <div
        className={cn(
          "p-4 cursor-pointer transition-all hover:bg-muted/50",
          priorityColors[notification.priority],
          !notification.is_read && "bg-blue-50/50 dark:bg-blue-950/20"
        )}
        onClick={() => handleNotificationClick(notification)}
        data-testid={`notification-item-${notification.notification_id}`}
      >
        <div className="flex gap-3">
          <div className={cn("p-2 rounded-lg shrink-0", config.bg)}>
            <Icon className={cn("h-4 w-4", config.color)} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <p className={cn(
                "text-sm font-medium truncate",
                !notification.is_read && "font-semibold"
              )}>
                {notification.title}
              </p>
              {!notification.is_read && (
                <span className="h-2 w-2 rounded-full bg-blue-500 shrink-0 mt-1.5" />
              )}
            </div>
            <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">
              {notification.message}
            </p>
            <div className="flex items-center gap-2 mt-2">
              <Clock className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">
                {formatTime(notification.created_at)}
              </span>
              {notification.priority === 'urgent' && (
                <Badge variant="destructive" className="text-xs py-0 px-1">Urgent</Badge>
              )}
              {notification.priority === 'high' && (
                <Badge variant="outline" className="text-xs py-0 px-1 border-orange-500 text-orange-500">High</Badge>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Hidden audio element for notification sound */}
      <audio ref={audioRef} preload="auto">
        <source src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2Onq+3tKaYjH94cXF4gIqTm6Oop6KalYyFgHp2dHV4fIOJj5OWmJiXlZKPi4eDgH58e3t8foCChYiKjI2Oj46OjYuJh4WDgYB/fn5+f4CCg4WGh4iIiIiHhoWEg4KBgH9/f39/gIGBgoODhIWFhYWFhISDgoKBgYCAgICAgIGBgoKCg4ODg4ODg4KCgoGBgYGAgICAgICAgIGBgYGBgoKCgoKCgoKCgYGBgYGAgICAgA==" type="audio/wav" />
      </audio>

      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            data-testid="notification-bell"
          >
            <Bell className="h-5 w-5" />
            {unreadCount.total_unread > 0 && (
              <span className={cn(
                "absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] text-xs font-bold rounded-full px-1",
                unreadCount.urgent > 0 ? "bg-red-500 text-white animate-pulse" : "bg-blue-500 text-white"
              )}>
                {unreadCount.total_unread > 99 ? '99+' : unreadCount.total_unread}
              </span>
            )}
          </Button>
        </SheetTrigger>

        <SheetContent className="w-full sm:max-w-md p-0 flex flex-col" data-testid="notification-panel">
          <SheetHeader className="p-4 border-b">
            <div className="flex items-center justify-between">
              <SheetTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notifications
                {unreadCount.total_unread > 0 && (
                  <Badge variant="secondary">{unreadCount.total_unread} new</Badge>
                )}
              </SheetTitle>
              <div className="flex items-center gap-2">
                {/* WebSocket connection status indicator */}
                <div 
                  className={cn(
                    "flex items-center gap-1 text-xs px-2 py-1 rounded-full",
                    wsConnected ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                  )}
                  title={wsConnected ? "Real-time updates active" : "Using periodic refresh"}
                >
                  {wsConnected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
                  <span className="hidden sm:inline">{wsConnected ? "Live" : "Polling"}</span>
                </div>
                {/* Push notification toggle */}
                {pushSupported && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handlePushToggle}
                    disabled={pushLoading || pushEnabling}
                    title={pushSubscribed ? 'Disable background notifications' : 'Enable background notifications'}
                    className={cn(
                      pushSubscribed && "text-green-600 dark:text-green-400"
                    )}
                    data-testid="push-toggle-btn"
                  >
                    {pushSubscribed ? <BellRing className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  title={soundEnabled ? 'Mute notifications' : 'Unmute notifications'}
                >
                  {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <Settings className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={markAllAsRead}>
                      <CheckCheck className="h-4 w-4 mr-2" />
                      Mark all as read
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {pushSupported && (
                      <DropdownMenuItem onClick={handlePushToggle} disabled={pushLoading || pushEnabling}>
                        {pushSubscribed ? <BellOff className="h-4 w-4 mr-2" /> : <BellRing className="h-4 w-4 mr-2" />}
                        {pushSubscribed ? 'Disable background alerts' : 'Enable background alerts'}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => navigate('/admin/settings?tab=notifications')}>
                      <Settings className="h-4 w-4 mr-2" />
                      Notification settings
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-2 mt-3">
              <Select value={daysFilter} onValueChange={setDaysFilter}>
                <SelectTrigger className="w-32 h-8 text-xs">
                  <Calendar className="h-3 w-3 mr-1" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Today</SelectItem>
                  <SelectItem value="1">Yesterday</SelectItem>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="14">Last 14 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">
                {totalCount} notification{totalCount !== 1 ? 's' : ''}
              </span>
            </div>
          </SheetHeader>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
            <TabsList className="mx-4 mt-2 grid grid-cols-5 h-8">
              <TabsTrigger value="all" className="text-xs">All</TabsTrigger>
              <TabsTrigger value="unread" className="text-xs">Unread</TabsTrigger>
              <TabsTrigger value="announcement" className="text-xs">
                <Megaphone className="h-3 w-3" />
              </TabsTrigger>
              <TabsTrigger value="alert" className="text-xs">
                <AlertTriangle className="h-3 w-3" />
              </TabsTrigger>
              <TabsTrigger value="score" className="text-xs">
                <Star className="h-3 w-3" />
              </TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="flex-1 mt-0 data-[state=active]:flex flex-col">
              <ScrollArea className="flex-1">
                <div className="divide-y">
                  {loading && notifications.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                      <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2" />
                      Loading notifications...
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                      <Bell className="h-12 w-12 mx-auto mb-3 opacity-20" />
                      <p className="font-medium">No notifications</p>
                      <p className="text-sm">You're all caught up!</p>
                    </div>
                  ) : (
                    <>
                      {notifications.map((notification) => (
                        <NotificationItem
                          key={notification.notification_id}
                          notification={notification}
                        />
                      ))}
                      {hasMore && (
                        <div className="p-4">
                          <Button
                            variant="outline"
                            className="w-full"
                            onClick={loadMore}
                            disabled={loading}
                          >
                            {loading ? 'Loading...' : 'Load more'}
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>

          {/* Quick stats footer */}
          {unreadCount.total_unread > 0 && (
            <div className="p-3 border-t bg-muted/30 flex items-center justify-between text-xs">
              <div className="flex items-center gap-3">
                {unreadCount.urgent > 0 && (
                  <span className="flex items-center gap-1 text-red-500">
                    <AlertTriangle className="h-3 w-3" />
                    {unreadCount.urgent} urgent
                  </span>
                )}
                {unreadCount.high > 0 && (
                  <span className="flex items-center gap-1 text-orange-500">
                    <Zap className="h-3 w-3" />
                    {unreadCount.high} high priority
                  </span>
                )}
              </div>
              <Button variant="link" size="sm" className="text-xs h-auto p-0" onClick={markAllAsRead}>
                Clear all
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
